import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import { CheckCircle, XCircle, AlertCircle, RefreshCw, ChevronDown, ChevronUp, Building2, MapPin, Calendar, Briefcase, Trash2, Users } from 'lucide-react';

const AdminCustomersPage = ({ adminToken }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [verifyingGst, setVerifyingGst] = useState(null);
  const [deletingCustomer, setDeletingCustomer] = useState(null);
  const [cleaningDuplicates, setCleaningDuplicates] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/admin/customers`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (userId, status) => {
    try {
      await axios.put(
        `${API}/admin/customers/${userId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success(`Customer ${status}!`);
      fetchCustomers();
    } catch (error) {
      console.error('Failed to update status', error);
      toast.error('Failed to update status');
    }
  };

  const handleCleanupDuplicates = async () => {
    if (!window.confirm('This will remove duplicate customers with the same phone number (keeping the oldest). Continue?')) {
      return;
    }
    
    setCleaningDuplicates(true);
    try {
      const response = await axios.post(
        `${API}/admin/customers/cleanup-duplicates`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success(response.data.message);
      if (response.data.removed && response.data.removed.length > 0) {
        toast.info(`Removed: ${response.data.removed.join(', ')}`);
      }
      fetchCustomers();
    } catch (error) {
      console.error('Failed to cleanup duplicates', error);
      toast.error('Failed to cleanup duplicates');
    } finally {
      setCleaningDuplicates(false);
    }
  };

  const handleReverifyGst = async (userId) => {
    setVerifyingGst(userId);
    try {
      const response = await axios.post(
        `${API}/admin/customers/${userId}/reverify-gst`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      if (response.data.verified) {
        toast.success('GST Verified Successfully!');
      } else {
        toast.warning(response.data.message || 'GST could not be verified. Check details.');
      }
      fetchCustomers();
    } catch (error) {
      console.error('Failed to re-verify GST', error);
      toast.error(error.response?.data?.detail || 'Failed to re-verify GST');
    } finally {
      setVerifyingGst(null);
    }
  };

  const handleManualVerifyGst = async (userId, businessName) => {
    if (!window.confirm(`Manually verify GST for "${businessName}"? Only do this if you have verified the GST number externally.`)) {
      return;
    }
    
    setVerifyingGst(userId);
    try {
      const response = await axios.post(
        `${API}/admin/customers/${userId}/manual-verify-gst`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success('GST manually verified!');
      fetchCustomers();
    } catch (error) {
      console.error('Failed to manually verify GST', error);
      toast.error(error.response?.data?.detail || 'Failed to manually verify GST');
    } finally {
      setVerifyingGst(null);
    }
  };

  const handleVerifyGst = async (userId) => {
    setVerifyingGst(userId);
    try {
      const response = await axios.post(
        `${API}/admin/customers/${userId}/verify-gst`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success(response.data.gst_details.verified ? 'GST Verified Successfully!' : 'GST Verification Failed - Details Updated');
      fetchCustomers();
    } catch (error) {
      console.error('Failed to verify GST', error);
      toast.error('Failed to verify GST');
    } finally {
      setVerifyingGst(null);
    }
  };

  const handleDeleteCustomer = async (userId, businessName) => {
    if (!window.confirm(`Are you sure you want to delete "${businessName}"? This action cannot be undone.`)) {
      return;
    }
    
    setDeletingCustomer(userId);
    try {
      await axios.delete(
        `${API}/admin/customers/${userId}`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success(`Customer "${businessName}" deleted successfully`);
      fetchCustomers();
    } catch (error) {
      console.error('Failed to delete customer', error);
      toast.error('Failed to delete customer');
    } finally {
      setDeletingCustomer(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getGstStatusColor = (gstDetails) => {
    if (!gstDetails) return 'text-gray-500';
    if (gstDetails.verified && gstDetails.gst_status === 'Active') return 'text-green-600';
    if (gstDetails.verified) return 'text-yellow-600';
    return 'text-red-500';
  };

  const getGstStatusIcon = (gstDetails) => {
    if (!gstDetails) return <AlertCircle className="w-4 h-4 text-gray-400" />;
    if (gstDetails.verified && gstDetails.gst_status === 'Active') return <CheckCircle className="w-4 h-4 text-green-600" />;
    if (gstDetails.verified) return <AlertCircle className="w-4 h-4 text-yellow-600" />;
    return <XCircle className="w-4 h-4 text-red-500" />;
  };

  const toggleExpand = (customerId) => {
    setExpandedCustomer(expandedCustomer === customerId ? null : customerId);
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <div className="py-8" data-testid="admin-customers-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
        <div>
          <h1 className="text-4xl font-heading font-bold mb-2">Customers Management</h1>
          <p className="text-muted-foreground">View all registered B2B customers and their GST details</p>
        </div>
        <button
          onClick={handleCleanupDuplicates}
          disabled={cleaningDuplicates}
          className="flex items-center gap-2 bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50"
          data-testid="cleanup-duplicates-btn"
        >
          <Users size={18} className={cleaningDuplicates ? 'animate-spin' : ''} />
          {cleaningDuplicates ? 'Cleaning...' : 'Remove Duplicates'}
        </button>
      </div>

      {customers.length === 0 ? (
        <div className="text-center py-12 bg-white border border-border">
          <p className="text-lg text-muted-foreground">No customers yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {customers.map((customer) => (
            <motion.div
              key={customer.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-border overflow-hidden"
              data-testid={`customer-${customer.id}`}
            >
              {/* Main Customer Info Row */}
              <div className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Left: Basic Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-heading font-semibold">{customer.business_name}</h3>
                      <span className={`px-3 py-1 text-xs font-semibold border ${getStatusColor(customer.status)} uppercase`}>
                        {customer.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                      <div className="min-w-0">
                        <span className="text-muted-foreground block">Contact:</span>
                        <p className="font-medium truncate">{customer.contact_person}</p>
                      </div>
                      <div className="min-w-0">
                        <span className="text-muted-foreground block">Email:</span>
                        <p className="font-medium truncate" title={customer.email}>{customer.email}</p>
                      </div>
                      <div className="min-w-0">
                        <span className="text-muted-foreground block">Phone:</span>
                        <p className="font-medium">{customer.phone}</p>
                      </div>
                    </div>
                  </div>

                  {/* Right: GST Status & Actions */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                    {/* GST Status Badge */}
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                      customer.gst_details?.verified ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                    }`}>
                      {getGstStatusIcon(customer.gst_details)}
                      <div className="text-sm">
                        <p className="font-semibold">{customer.gst_number}</p>
                        <p className={`text-xs ${getGstStatusColor(customer.gst_details)}`}>
                          {customer.gst_details?.verified 
                            ? `Verified • ${customer.gst_details.gst_status || 'Status Unknown'}`
                            : 'Not Verified'}
                        </p>
                      </div>
                    </div>

                    {/* Verify GST Button */}
                    <button
                      onClick={() => handleReverifyGst(customer.id)}
                      disabled={verifyingGst === customer.id}
                      className="flex items-center gap-1 px-3 py-2 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors disabled:opacity-50"
                      data-testid={`verify-gst-${customer.id}`}
                      title="Verify using GST API"
                    >
                      <RefreshCw className={`w-3 h-3 ${verifyingGst === customer.id ? 'animate-spin' : ''}`} />
                      {verifyingGst === customer.id ? 'Verifying...' : 'Verify GST'}
                    </button>

                    {/* Expand Button */}
                    <button
                      onClick={() => toggleExpand(customer.id)}
                      className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 hover:bg-gray-50 rounded transition-colors"
                    >
                      {expandedCustomer === customer.id ? (
                        <>Hide Details <ChevronUp className="w-4 h-4" /></>
                      ) : (
                        <>View Details <ChevronDown className="w-4 h-4" /></>
                      )}
                    </button>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border">
                  {customer.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleStatusChange(customer.id, 'approved')}
                        className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700 text-white py-2 px-4 text-sm font-semibold transition-colors"
                        data-testid={`approve-${customer.id}`}
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleStatusChange(customer.id, 'rejected')}
                        className="flex-1 sm:flex-none bg-red-600 hover:bg-red-700 text-white py-2 px-4 text-sm font-semibold transition-colors"
                        data-testid={`reject-${customer.id}`}
                      >
                        Reject
                      </button>
                    </>
                  )}
                  {customer.status === 'approved' && (
                    <button
                      onClick={() => handleStatusChange(customer.id, 'rejected')}
                      className="bg-red-600 hover:bg-red-700 text-white py-2 px-4 text-sm font-semibold transition-colors"
                    >
                      Revoke Access
                    </button>
                  )}
                  {customer.status === 'rejected' && (
                    <button
                      onClick={() => handleStatusChange(customer.id, 'approved')}
                      className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 text-sm font-semibold transition-colors"
                    >
                      Approve Now
                    </button>
                  )}
                  
                  {/* Delete Button - Always visible */}
                  <button
                    onClick={() => handleDeleteCustomer(customer.id, customer.business_name)}
                    disabled={deletingCustomer === customer.id}
                    className="flex items-center gap-2 bg-gray-800 hover:bg-gray-900 text-white py-2 px-4 text-sm font-semibold transition-colors disabled:opacity-50 ml-auto"
                    data-testid={`delete-${customer.id}`}
                  >
                    <Trash2 className={`w-4 h-4 ${deletingCustomer === customer.id ? 'animate-pulse' : ''}`} />
                    {deletingCustomer === customer.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>

              {/* Expandable GST Details Section */}
              <AnimatePresence>
                {expandedCustomer === customer.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 bg-gray-50 border-t border-border">
                      <h4 className="text-lg font-semibold mt-4 mb-4 flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-luxury-gold" />
                        GST Business Details
                      </h4>

                      {customer.gst_details ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {/* Legal Name */}
                          <div className="bg-white p-4 rounded-lg border">
                            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Legal Business Name</p>
                            <p className="font-semibold text-lg">{customer.gst_details.legal_name || '-'}</p>
                          </div>

                          {/* Trade Name */}
                          <div className="bg-white p-4 rounded-lg border">
                            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Trade Name</p>
                            <p className="font-semibold text-lg">{customer.gst_details.trade_name || '-'}</p>
                          </div>

                          {/* Business Type */}
                          <div className="bg-white p-4 rounded-lg border">
                            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Business Type</p>
                            <p className="font-semibold">{customer.gst_details.business_type || '-'}</p>
                          </div>

                          {/* Nature of Business - Important for Trade Check */}
                          <div className="bg-white p-4 rounded-lg border md:col-span-2 lg:col-span-3 border-luxury-gold border-2">
                            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-2">
                              <Briefcase className="w-4 h-4 text-luxury-gold" />
                              Nature of Business (Trade Type)
                            </p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {customer.gst_details.nature_of_business?.length > 0 ? (
                                customer.gst_details.nature_of_business.map((trade, idx) => (
                                  <span 
                                    key={idx} 
                                    className="px-3 py-1 bg-luxury-gold/10 text-luxury-gold border border-luxury-gold/30 rounded-full text-sm font-medium"
                                  >
                                    {trade}
                                  </span>
                                ))
                              ) : (
                                <span className="text-gray-500">Not available</span>
                              )}
                            </div>
                          </div>

                          {/* GST Status */}
                          <div className="bg-white p-4 rounded-lg border">
                            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">GST Status</p>
                            <p className={`font-semibold ${
                              customer.gst_details.gst_status === 'Active' ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {customer.gst_details.gst_status || '-'}
                            </p>
                          </div>

                          {/* Registration Date */}
                          <div className="bg-white p-4 rounded-lg border">
                            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1">
                              <Calendar className="w-3 h-3" /> Registration Date
                            </p>
                            <p className="font-semibold">{customer.gst_details.registration_date || '-'}</p>
                          </div>

                          {/* State */}
                          <div className="bg-white p-4 rounded-lg border">
                            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">State</p>
                            <p className="font-semibold">{customer.gst_details.state || '-'}</p>
                          </div>

                          {/* Registered Address */}
                          <div className="bg-white p-4 rounded-lg border md:col-span-2 lg:col-span-3">
                            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> Registered Address
                            </p>
                            <p className="font-medium">{customer.gst_details.address || '-'}</p>
                          </div>

                          {/* Verification Info */}
                          <div className="bg-white p-4 rounded-lg border md:col-span-2 lg:col-span-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-xs text-muted-foreground">Last Verified</p>
                                <p className="text-sm">
                                  {customer.gst_details.verification_date 
                                    ? new Date(customer.gst_details.verification_date).toLocaleString()
                                    : 'Never'}
                                </p>
                              </div>
                              {customer.gst_details.error && (
                                <div className="text-right">
                                  <p className="text-xs text-red-500">Verification Note</p>
                                  <p className="text-sm text-red-600">{customer.gst_details.error}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <p className="text-yellow-800 flex items-center gap-2">
                            <AlertCircle className="w-5 h-5" />
                            GST details not available. Click "Verify GST" to fetch business details.
                          </p>
                        </div>
                      )}

                      {/* Customer Provided Info */}
                      <div className="mt-6">
                        <h4 className="text-lg font-semibold mb-4">Customer Provided Information</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-white p-4 rounded-lg border">
                            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Provided Address</p>
                            <p className="font-medium">{customer.address}</p>
                          </div>
                          <div className="bg-white p-4 rounded-lg border">
                            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Joined On</p>
                            <p className="font-medium">{new Date(customer.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCustomersPage;
